import React, { useState, useRef, useMemo, useEffect } from 'react';
import { AuditLog } from '../services/api/domains/audit/types';
import { useAuditLogsPage } from '../services/api/domains/audit/hooks';
import TableSearch from '../components/TableSearch';
import {
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from '../components/TableSearch/types';
import { auditLogsFieldConfig } from '../config/searchConfigs';
import LgmDataTable, { Column } from '../components/LgmDataTable';

const PAGE_SIZE = 50;

// Helper function to get operation type badge styles
const getOperationTypeStyle = (
  operationType: AuditLog['operation_type']
): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
  };

  switch (operationType) {
    case 'INSERT':
      return {
        ...baseStyle,
        backgroundColor: '#dcfce7',
        color: '#166534',
      };
    case 'UPDATE':
      return {
        ...baseStyle,
        backgroundColor: '#dbeafe',
        color: '#1e40af',
      };
    case 'DELETE':
      return {
        ...baseStyle,
        backgroundColor: '#fee2e2',
        color: '#991b1b',
      };
    case 'MERGE':
      return {
        ...baseStyle,
        backgroundColor: '#fef3c7',
        color: '#92400e',
      };
    default:
      return baseStyle;
  }
};

const AuditLogs: React.FC = () => {
  // Advanced search only - no basic search
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());
  const [page, setPage] = useState(0);
  const pageRef = useRef(page);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof AuditLog;
    direction: 'ASC' | 'DESC';
  }>({
    key: 'changed_date',
    direction: 'DESC',
  });

  const auditLogsParams = useMemo(
    () => ({
      advancedSearch: advancedSearchQuery,
      sortBy: sortConfig?.key,
      sortDirection: sortConfig?.direction,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [advancedSearchQuery, page, sortConfig]
  );

  const { data: auditLogs, total } = useAuditLogsPage(auditLogsParams);

  const auditLogsColumns = useMemo(
    () =>
      [
        {
          key: 'table_name',
          label: 'Table',
          width: '10%',
        },
        {
          key: 'record_id',
          label: 'Record ID',
          width: '5%',
        },
        {
          key: 'operation_type',
          label: 'Operation',
          width: '6%',
          render: (row: AuditLog) => (
            <span style={getOperationTypeStyle(row.operation_type)}>
              {row.operation_type}
            </span>
          ),
        },
        {
          key: 'field_name',
          label: 'Field',
          width: '10%',
          render: (row: AuditLog) => row.field_name || '—',
        },
        {
          key: 'old_value',
          label: 'Old Value',
          textLength: (row: AuditLog) => row.old_value?.length || 0,
          render: (row: AuditLog, isExpanded: boolean) => {
            if (!row.old_value) {
              return <span style={styles.emptyValueStyle}>—</span>;
            }

            // Try to parse as JSON for pretty printing when expanded
            try {
              const parsed = JSON.parse(row.old_value);
              return (
                <span style={styles.monospaceTextStyle}>
                  {isExpanded ? JSON.stringify(parsed, null, 2) : row.old_value}
                </span>
              );
            } catch {
              // Not JSON, render as-is
              return (
                <span style={styles.monospaceTextStyle}>{row.old_value}</span>
              );
            }
          },
        },
        {
          key: 'new_value',
          label: 'New Value',
          textLength: (row: AuditLog) => row.new_value?.length || 0,
          render: (row: AuditLog, isExpanded: boolean) => {
            if (!row.new_value) {
              return <span style={styles.emptyValueStyle}>—</span>;
            }

            // Try to parse as JSON for pretty printing when expanded
            try {
              const parsed = JSON.parse(row.new_value);
              return (
                <span style={styles.monospaceTextStyle}>
                  {isExpanded ? JSON.stringify(parsed, null, 2) : row.new_value}
                </span>
              );
            } catch {
              // Not JSON, render as-is
              return (
                <span style={styles.monospaceTextStyle}>{row.new_value}</span>
              );
            }
          },
        },
        {
          key: 'changed_by',
          label: 'Changed By',
          width: '10%',
        },
        {
          key: 'changed_date',
          label: 'Changed Date',
          width: '9%',
          render: (row: AuditLog) =>
            new Date(row.changed_date).toLocaleString(),
        },
      ] as Column<AuditLog>[],
    []
  );

  // Reset page when search changes
  useEffect(() => {
    if (pageRef.current !== 0) {
      console.log('Resetting page to 0 due to search change');
      setPage(0);
    }
  }, [advancedSearchQuery]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // pagination labels
  const currentPageLength = auditLogs.length;
  const startIdx = currentPageLength === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIdx =
    currentPageLength === 0 ? 0 : page * PAGE_SIZE + currentPageLength;

  return (
    <div>
      <div style={styles.stickyTopBarStyle}>
        <div style={styles.searchContainerStyle}>
          <TableSearch
            mode="advancedOnly"
            onModeChange={() => {}} // No mode switching - advanced only
            basicSearchTerm=""
            onBasicSearchChange={() => {}}
            onBasicSearchClear={() => {}}
            basicPlaceholder=""
            advancedFieldConfigs={auditLogsFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={(query: AdvancedSearchQuery) => {
              setAdvancedSearchQuery(query);
              setPage(0);
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
              setPage(0);
            }}
          />
        </div>
      </div>

      <div>
        <LgmDataTable<AuditLog>
          data={auditLogs}
          columns={auditLogsColumns}
          sortConfig={sortConfig}
          onSort={setSortConfig}
          paginationInfo={{
            startIdx,
            endIdx,
            count: total || 0,
            page,
            setPage,
          }}
          // No edit or delete capabilities for audit logs
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  stickyTopBarStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  monospaceTextStyle: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  } as React.CSSProperties,
  emptyValueStyle: {
    color: '#9ca3af',
  } as React.CSSProperties,
};

export default AuditLogs;
